import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import {
  gaConfigured,
  getConsent,
  setConsent,
  onConsentChange,
  onOpenBanner,
} from "../lib/analytics";

/**
 * Canadian-compliant cookie consent banner.
 *
 * - Hidden when GA isn't configured (no point asking).
 * - Hidden on /admin/* (we don't run analytics there).
 * - Shows on first visit, or when the footer "Manage cookies" link dispatches
 *   the global open-banner event.
 * - Choice (accepted / declined) persisted in localStorage for 12 months.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [showOptions, setShowOptions] = useState(false);

  const isAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (!gaConfigured() || isAdmin) return;
    const c = getConsent();
    setCurrentStatus(c?.status || null);
    if (!c) {
      // First visit — show the banner after the page has settled.
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [isAdmin]);

  useEffect(() => {
    return onOpenBanner(() => {
      setCurrentStatus(getConsent()?.status || null);
      setShowOptions(false);
      setVisible(true);
    });
  }, []);

  useEffect(() => {
    return onConsentChange((e) => {
      setCurrentStatus(e?.detail?.status || null);
    });
  }, []);

  const accept = useCallback(() => {
    setConsent(true);
    setVisible(false);
  }, []);
  const decline = useCallback(() => {
    setConsent(false);
    setVisible(false);
  }, []);
  const dismiss = useCallback(() => setVisible(false), []);

  if (!gaConfigured() || isAdmin || !visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-5 sm:pb-5"
    >
      <div
        className="pointer-events-auto mx-auto w-full max-w-2xl rounded-md border border-ridge-border bg-white shadow-[0_24px_48px_-12px_rgba(28,28,28,0.35)] animate-cookie-rise"
      >
        <div className="relative p-5 sm:p-6">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded text-ridge-muted hover:bg-ridge-stone hover:text-ridge-charcoal"
            data-testid="cookie-consent-dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ridge-amber/15 text-ridge-amber">
              <Cookie className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-base font-semibold leading-tight text-ridge-charcoal sm:text-lg">
                We value your privacy
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-ridge-text">
                We use cookies to analyze how visitors use our site and improve your
                experience. You can change your choice anytime via the cookie
                settings link in the footer. See our{" "}
                <Link to="/privacy" className="font-semibold text-ridge-amber underline-offset-2 hover:underline">
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>

              {showOptions ? (
                <div
                  className="mt-4 rounded-md border border-ridge-border bg-ridge-stone/60 p-4 text-sm text-ridge-text"
                  data-testid="cookie-consent-customize"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ridge-charcoal">Analytics cookies</p>
                      <p className="mt-1 text-xs text-ridge-muted">
                        Google Analytics 4 — measures page views and aggregated engagement.
                        IP addresses are anonymised.
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-muted">
                      Optional
                    </span>
                  </div>
                  <div className="mt-3 flex items-start justify-between gap-3 border-t border-ridge-border pt-3 text-xs text-ridge-muted">
                    <div className="min-w-0">
                      <p className="font-semibold text-ridge-charcoal">Strictly necessary</p>
                      <p className="mt-1">Required for the site to function (e.g., your chat session). Always on.</p>
                    </div>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                      Always on
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setShowOptions((v) => !v)}
                  className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal hover:text-ridge-amber"
                  data-testid="cookie-consent-manage"
                >
                  {showOptions ? "Hide details" : "Manage"}
                </button>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={decline}
                    className="rounded-md border border-ridge-charcoal bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal transition-colors hover:bg-ridge-charcoal hover:text-white"
                    data-testid="cookie-consent-decline"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={accept}
                    className="rounded-md bg-ridge-amber px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-ridge-amber-dark"
                    data-testid="cookie-consent-accept"
                  >
                    Accept
                  </button>
                </div>
              </div>

              {currentStatus ? (
                <p className="mt-3 text-[11px] text-ridge-muted">
                  Current preference: <strong className="font-semibold text-ridge-charcoal">{currentStatus}</strong>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
